/**
 * Infinity order summary breakdown (Producción / Marketing).
 *
 * The summary splits an Infinity aligners line item into "Producción" and a
 * fixed "Marketing" markup. Historically both amounts were HARDCODED per
 * variant and country in CardProduct, so they only matched when the line item
 * was charged at the catalog price. With a per-center price override the real
 * charge can differ (e.g. 14,000 instead of the 14,713 catalog), and the
 * hardcoded split (11,713 + 3,000 = 14,713) no longer adds up to the total.
 *
 * This derives the split from the REAL unit price instead:
 *   Marketing  = fixed markup (per country, mkt vs plain infinity)
 *   Producción = unitPrice − Marketing
 * so the two lines always sum to the price actually charged. At the catalog
 * price it reproduces the previous hardcoded values exactly.
 *
 * Returns null (→ caller shows the single price, no split) for any non-Infinity
 * variant, unsupported country, or invalid price, so it can never render a
 * misleading breakdown.
 */

// Fixed "Marketing" markup, in the country's own currency.
const MARKETING_MARKUP = {
  MX: { mkt: 3000, plain: 1000 },
  CO: { mkt: 300000, plain: 100000 },
  PE: { mkt: 699, plain: 299 },
};

// Marketing-sourced (moons) variants carry the larger markup; plain `infinity`
// (no tier / doctor-source aggregate) carries the smaller one.
const MKT_VARIANTS = ['infinity_mkt', 'infinity_lite_mkt', 'infinity_full_mkt'];
const INFINITY_VARIANTS = ['infinity', ...MKT_VARIANTS];

const formatBreakdownMoney = (value, country) => {
  const [intPart, decPart] = Number(value).toFixed(2).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '#');
  const esStyle = country === 'CO' || country === 'PE';
  return esStyle
    ? `${grouped.replace(/#/g, '.')},${decPart}`
    : `${grouped.replace(/#/g, ',')}.${decPart}`;
};

const getInfinityBreakdown = (unitPrice, country, variant) => {
  if (!INFINITY_VARIANTS.includes(variant)) return null;

  const markups = MARKETING_MARKUP[country];
  if (!markups) return null;

  const price = Number(unitPrice);
  if (!Number.isFinite(price) || price <= 0) return null;

  const marketing = MKT_VARIANTS.includes(variant) ? markups.mkt : markups.plain;

  return {
    produccion: formatBreakdownMoney(price - marketing, country),
    marketing: formatBreakdownMoney(marketing, country),
  };
};

export default getInfinityBreakdown;
